import { randomId } from "@mantine/hooks";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import {
  type IMedicalHistoryModalProps,
  IMedicalLeaveEntry,
  type IMedicalLeaveHistory,
  type IMedicalOthersSection,
  type IMedicalPatientInformation,
} from "../../components/pages/Settings/YachtSetUp/MedicalHistory/sections/MedicalSections.model";
import { getMedIdType } from "../../components/pages/Settings/YachtSetUp/MedicalHistory/utils/getMedIdType";
import { deepClone } from "../../utils";
import { clearInput, insertId } from "../utils/medicalHistory.utils";

export interface MedicalHistorySlice {
  patientInformation: IMedicalPatientInformation;
  leaveHistory: IMedicalLeaveHistory;
  others: IMedicalOthersSection[];
  modalProps: IMedicalHistoryModalProps | null;
  hasMedCardStatement: boolean;
}

const initialState: MedicalHistorySlice = {
  patientInformation: {
    patientId: null,
    URN: 0,
    name: "",
    attachments: [],
    inputs: [],
  },
  leaveHistory: {
    medLeaveHistory: { daysOnBoard: 0, daysOffLeave: 0, list: [] },
    inputs: [],
  },
  others: [],
  modalProps: null,
  hasMedCardStatement: false,
};

export const medicalHistorySlice = createSlice({
  name: "medicalHistory",
  initialState,
  reducers: {
    setMedicalHistoryData: (state, action) => {
      if (
        !action.payload?.patientInformation ||
        !action.payload?.leaveHistory ||
        !action.payload?.others
      )
        return;
      const { patientInformation, leaveHistory, others, hasMedCardStatement } =
        action.payload;
      state.hasMedCardStatement = hasMedCardStatement;
      state.patientInformation = {
        ...patientInformation,
        inputs: patientInformation.inputs.map(insertId),
      };

      state.leaveHistory = {
        ...leaveHistory,
        inputs: leaveHistory.inputs.map(insertId),
      };
      state.others = others.map((section: any) => ({
        ...section,
        sectionId: randomId(),
        rows: section.rows.map((row: any) => ({
          ...row,
          inputs: row.inputs.map(insertId),
        })),
      }));
    },
    setTotalPeriodData: (
      state,
      action: PayloadAction<{
        daysOnBoard: number;
        daysOffLeave: number;
        list: IMedicalLeaveEntry[];
      }>,
    ) => {
      state.leaveHistory = {
        ...state.leaveHistory,
        medLeaveHistory: {
          ...state.leaveHistory.medLeaveHistory,
          ...action.payload,
        },
      };
    },
    addDynamicRow: (state, action) => {
      const { sectionId } = action.payload;
      const section = state.others.find(
        section => section.sectionId === sectionId,
      );
      if (!section) return;
      const idType = getMedIdType(section.dataKey);

      const clearedInputs = deepClone(section.rows[0].inputs).map(clearInput);
      const clearedRow = {
        ...section.rows[0],
        inputs: clearedInputs,
        [idType]: null,
      };

      section.rows.push(clearedRow);
    },
    removeDynamicRow: (state, action) => {
      const { sectionId, rowIndex } = action.payload;
      const section = state.others.find(
        section => section.sectionId === sectionId,
      );
      if (section) {
        section.rows.splice(rowIndex, 1);
      }
    },
    setModalProps: (
      state,
      action: PayloadAction<IMedicalHistoryModalProps | null>,
    ) => {
      state.modalProps = action.payload;
    },
    resetMedState: state => initialState,
  },
});

export const {
  setMedicalHistoryData,
  resetMedState,
  addDynamicRow,
  removeDynamicRow,
  setModalProps,
  setTotalPeriodData,
} = medicalHistorySlice.actions;

export default medicalHistorySlice.reducer;
